import { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import adminService from '../../services/admin.service';
import type {
  CallMetadataConfig,
  CustomRuleCreatePayload,
  CustomRuleResponse,
  CustomRuleType,
  CustomRuleUpdatePayload,
  LLMQuestionConfig,
  PhraseForbiddenConfig,
  PhraseRequiredConfig,
  PhraseSpeaker,
  PhraseMatcher,
} from '../../types/custom-rule.types';
import type { RuleCategoryKey, RuleSeverity } from '../../types/policy.types';

interface CustomRuleEditorModalProps {
  open: boolean;
  userId: string;
  initial?: CustomRuleResponse | null;   // null → create mode
  onClose: () => void;
  onSaved: (rule: CustomRuleResponse) => void;
}

type CallMetadataCheck = 'calling_hours' | 'max_duration' | 'min_duration' | 'frequency_cap';

type FormState = {
  name: string;
  description: string;
  category: RuleCategoryKey;
  severity: RuleSeverity;
  rule_type: CustomRuleType;
  // phrase rule fields
  phrases: string;          // newline-separated for editing
  matcher: PhraseMatcher;
  speaker: PhraseSpeaker;
  must_appear_within_seconds: string;
  only_after_seconds: string;
  pattern_type: 'literal' | 'regex';
  // llm_question fields
  llm_prompt: string;
  llm_expected_value: 'yes' | 'no';
  llm_speaker_focus: 'agent' | 'customer' | 'both';
  llm_estimated_input_tokens: string;
  llm_estimated_output_tokens: string;
  // call_metadata fields
  cm_check_type: CallMetadataCheck;
  cm_from_hour: string;
  cm_to_hour: string;
  cm_weekdays: number[];
  cm_consumer_tz_field: string;
  cm_fallback_tz: string;
  cm_seconds_threshold: string;
  cm_max_calls_per_window: string;
  cm_window_days: string;
  cm_grouping: 'phone_number' | 'customer_id';
  // shared
  jurisdictions: string;
  regulation_law: string;
  regulation_jurisdiction: string;
  regulation_citation: string;
};

const SEVERITY_OPTIONS: RuleSeverity[] = ['low', 'medium', 'high', 'critical'];
const CATEGORY_OPTIONS: { value: RuleCategoryKey; label: string }[] = [
  { value: 'compliance', label: 'Compliance' },
  { value: 'legal_risk', label: 'Legal Risk' },
  { value: 'qa', label: 'QA' },
];
const SUPPORTED_TYPES: { value: CustomRuleType; label: string; help: string }[] = [
  {
    value: 'phrase_required',
    label: 'Agent must say a phrase',
    help: 'Flag the call when the agent does not say one of these phrases (optionally within a time window).',
  },
  {
    value: 'phrase_forbidden',
    label: 'Agent must NOT say a phrase',
    help: 'Flag every time the agent says a banned phrase. Supports regex for patterns like card numbers.',
  },
  {
    value: 'call_metadata',
    label: 'Call metadata check',
    help: 'Calling hours / max duration / min duration / frequency cap. No transcript scan — uses call timestamp + duration only.',
  },
  {
    value: 'llm_question',
    label: 'LLM yes/no question',
    help: 'Adds a yes/no compliance question to the per-call LLM batch. Costs ~$0.000005/call per rule (Gemini Flash-Lite batch rate). Cap: 20 questions per call.',
  },
];

// Gemini 2.5 Flash-Lite batch tier rates (per cost_constants.py).
// Input $0.05/1M tokens, Output $0.20/1M tokens.
const LLM_INPUT_PER_M = 0.05;
const LLM_OUTPUT_PER_M = 0.20;

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const COMMON_TIMEZONES = [
  'America/Mexico_City',
  'America/Sao_Paulo',
  'America/Lima',
  'America/Santiago',
  'America/Bogota',
  'America/Argentina/Buenos_Aires',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
];

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  category: 'compliance',
  severity: 'medium',
  rule_type: 'phrase_required',
  phrases: '',
  matcher: 'semantic',
  speaker: 'agent',
  must_appear_within_seconds: '',
  only_after_seconds: '',
  pattern_type: 'literal',
  cm_check_type: 'calling_hours',
  cm_from_hour: '7',
  cm_to_hour: '22',
  cm_weekdays: [0, 1, 2, 3, 4],
  cm_consumer_tz_field: 'metadata.consumer_timezone',
  cm_fallback_tz: 'America/Mexico_City',
  cm_seconds_threshold: '900',
  cm_max_calls_per_window: '7',
  cm_window_days: '7',
  cm_grouping: 'phone_number',
  llm_prompt: '',
  llm_expected_value: 'yes',
  llm_speaker_focus: 'agent',
  llm_estimated_input_tokens: '80',
  llm_estimated_output_tokens: '10',
  jurisdictions: '',
  regulation_law: '',
  regulation_jurisdiction: '',
  regulation_citation: '',
};

function fromRule(r: CustomRuleResponse): FormState {
  const cfg = r.config;
  const base: FormState = {
    ...EMPTY_FORM,
    name: r.name,
    description: r.description ?? '',
    category: r.category,
    severity: r.severity,
    rule_type: cfg.rule_type as CustomRuleType,
    jurisdictions: r.jurisdictions.join(','),
    regulation_law: r.regulation_refs[0]?.law ?? '',
    regulation_jurisdiction: r.regulation_refs[0]?.jurisdiction ?? '',
    regulation_citation: r.regulation_refs[0]?.citation ?? '',
  };
  if (cfg.rule_type === 'phrase_required') {
    return {
      ...base,
      phrases: cfg.phrases.join('\n'),
      matcher: cfg.matcher,
      speaker: cfg.speaker,
      must_appear_within_seconds:
        cfg.must_appear_within_seconds != null ? String(cfg.must_appear_within_seconds) : '',
    };
  }
  if (cfg.rule_type === 'phrase_forbidden') {
    return {
      ...base,
      phrases: cfg.phrases.join('\n'),
      matcher: cfg.matcher,
      speaker: cfg.speaker,
      pattern_type: cfg.pattern_type,
      only_after_seconds:
        cfg.only_after_seconds != null ? String(cfg.only_after_seconds) : '',
    };
  }
  if (cfg.rule_type === 'llm_question') {
    return {
      ...base,
      llm_prompt: cfg.prompt,
      llm_expected_value: cfg.expected_value,
      llm_speaker_focus: cfg.speaker_focus ?? 'agent',
      llm_estimated_input_tokens: String(cfg.estimated_input_tokens ?? 80),
      llm_estimated_output_tokens: String(cfg.estimated_output_tokens ?? 10),
    };
  }
  if (cfg.rule_type === 'call_metadata') {
    return {
      ...base,
      cm_check_type: cfg.check_type,
      cm_from_hour: cfg.allowed_from_hour != null ? String(cfg.allowed_from_hour) : '',
      cm_to_hour: cfg.allowed_to_hour != null ? String(cfg.allowed_to_hour) : '',
      cm_weekdays: cfg.allowed_weekdays ?? [],
      cm_consumer_tz_field: cfg.consumer_timezone_field ?? 'metadata.consumer_timezone',
      cm_fallback_tz: cfg.fallback_timezone ?? 'America/Mexico_City',
      cm_seconds_threshold: cfg.seconds_threshold != null ? String(cfg.seconds_threshold) : '',
      cm_max_calls_per_window:
        cfg.max_calls_per_window != null ? String(cfg.max_calls_per_window) : '',
      cm_window_days: cfg.window_days != null ? String(cfg.window_days) : '',
      cm_grouping: cfg.grouping ?? 'phone_number',
    };
  }
  return base;
}

function toPayload(form: FormState): CustomRuleCreatePayload {
  const phrases = form.phrases
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

  let config:
    | PhraseRequiredConfig
    | PhraseForbiddenConfig
    | CallMetadataConfig
    | LLMQuestionConfig;
  if (form.rule_type === 'phrase_required') {
    config = {
      rule_type: 'phrase_required',
      phrases,
      matcher: form.matcher,
      speaker: form.speaker,
      must_appear_within_seconds: form.must_appear_within_seconds
        ? parseInt(form.must_appear_within_seconds, 10)
        : null,
    };
  } else if (form.rule_type === 'phrase_forbidden') {
    config = {
      rule_type: 'phrase_forbidden',
      phrases,
      pattern_type: form.pattern_type,
      matcher: form.matcher,
      speaker: form.speaker,
      only_after_seconds: form.only_after_seconds
        ? parseInt(form.only_after_seconds, 10)
        : null,
    };
  } else if (form.rule_type === 'llm_question') {
    config = {
      rule_type: 'llm_question',
      prompt: form.llm_prompt,
      expected_value: form.llm_expected_value,
      speaker_focus: form.llm_speaker_focus,
      estimated_input_tokens: parseInt(form.llm_estimated_input_tokens, 10) || 80,
      estimated_output_tokens: parseInt(form.llm_estimated_output_tokens, 10) || 10,
      require_evidence: true,
    };
  } else {
    // call_metadata
    const ct = form.cm_check_type;
    config = {
      rule_type: 'call_metadata',
      check_type: ct,
      allowed_from_hour:
        ct === 'calling_hours' && form.cm_from_hour ? parseInt(form.cm_from_hour, 10) : null,
      allowed_to_hour:
        ct === 'calling_hours' && form.cm_to_hour ? parseInt(form.cm_to_hour, 10) : null,
      allowed_weekdays:
        ct === 'calling_hours' && form.cm_weekdays.length > 0 ? form.cm_weekdays : null,
      consumer_timezone_field: form.cm_consumer_tz_field,
      fallback_timezone: form.cm_fallback_tz,
      seconds_threshold:
        ct === 'max_duration' || ct === 'min_duration'
          ? parseInt(form.cm_seconds_threshold, 10)
          : null,
      max_calls_per_window:
        ct === 'frequency_cap' ? parseInt(form.cm_max_calls_per_window, 10) : null,
      window_days: ct === 'frequency_cap' ? parseInt(form.cm_window_days, 10) : null,
      grouping: form.cm_grouping,
    };
  }

  const jurisdictions = form.jurisdictions
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);

  const regulation_refs =
    form.regulation_law && form.regulation_jurisdiction
      ? [
          {
            jurisdiction: form.regulation_jurisdiction.toUpperCase(),
            law: form.regulation_law,
            citation: form.regulation_citation || form.regulation_law,
            url: null,
          },
        ]
      : [];

  return {
    name: form.name,
    description: form.description || null,
    category: form.category,
    severity: form.severity,
    enabled: true,
    config,
    regulation_refs,
    jurisdictions,
  };
}

export default function CustomRuleEditorModal({
  open,
  userId,
  initial,
  onClose,
  onSaved,
}: CustomRuleEditorModalProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial ? fromRule(initial) : EMPTY_FORM);
      setError(null);
    }
  }, [open, initial]);

  const isEdit = Boolean(initial);
  const dirty = useMemo(() => {
    if (!isEdit) return form.name.trim().length > 0;
    return JSON.stringify(form) !== JSON.stringify(initial ? fromRule(initial) : EMPTY_FORM);
  }, [form, initial, isEdit]);

  async function handleSave() {
    setError(null);
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    const isPhraseRule =
      form.rule_type === 'phrase_required' || form.rule_type === 'phrase_forbidden';
    if (isPhraseRule && !form.phrases.trim()) {
      setError('At least one phrase is required.');
      return;
    }
    if (form.rule_type === 'llm_question') {
      if (!form.llm_prompt.trim() || form.llm_prompt.trim().length < 10) {
        setError('LLM prompt must be at least 10 characters.');
        return;
      }
    }
    if (form.rule_type === 'call_metadata') {
      if (form.cm_check_type === 'calling_hours' && (!form.cm_from_hour || !form.cm_to_hour)) {
        setError('Calling hours requires both From and To hours.');
        return;
      }
      if (
        (form.cm_check_type === 'max_duration' || form.cm_check_type === 'min_duration') &&
        !form.cm_seconds_threshold
      ) {
        setError('Duration threshold is required.');
        return;
      }
      if (
        form.cm_check_type === 'frequency_cap' &&
        (!form.cm_max_calls_per_window || !form.cm_window_days)
      ) {
        setError('Frequency cap requires max calls and window in days.');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = toPayload(form);
      const saved = isEdit && initial
        ? await adminService.updateCustomRule(userId, initial.id, payload as CustomRuleUpdatePayload)
        : await adminService.createCustomRule(userId, payload);
      toast.success(isEdit ? 'Rule updated.' : 'Rule created.');
      onSaved(saved);
      onClose();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save rule.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 translate-y-4" enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-4"
            >
              <Dialog.Panel className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-gray-200 p-5">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {isEdit ? 'Edit custom rule' : 'New custom rule'}
                  </Dialog.Title>
                  <button onClick={onClose} className="rounded p-1 hover:bg-gray-100">
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
                  <label className="sm:col-span-2">
                    <span className="block text-xs font-medium text-gray-700">Rule type</span>
                    <select
                      value={form.rule_type}
                      onChange={(e) => setForm({ ...form, rule_type: e.target.value as CustomRuleType })}
                      className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                      disabled={isEdit}
                    >
                      {SUPPORTED_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                    <span className="mt-1 block text-[11px] text-gray-500">
                      {SUPPORTED_TYPES.find((t) => t.value === form.rule_type)?.help}
                    </span>
                  </label>

                  <label>
                    <span className="block text-xs font-medium text-gray-700">Name</span>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                      placeholder="e.g., Recording notice"
                    />
                  </label>

                  <label>
                    <span className="block text-xs font-medium text-gray-700">Category</span>
                    <select
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value as RuleCategoryKey })}
                      className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                    >
                      {CATEGORY_OPTIONS.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span className="block text-xs font-medium text-gray-700">Severity</span>
                    <select
                      value={form.severity}
                      onChange={(e) => setForm({ ...form, severity: e.target.value as RuleSeverity })}
                      className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                    >
                      {SEVERITY_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </label>

                  {form.rule_type !== 'call_metadata' && (
                  <label>
                    <span className="block text-xs font-medium text-gray-700">Speaker</span>
                    <select
                      value={form.speaker}
                      onChange={(e) => setForm({ ...form, speaker: e.target.value as PhraseSpeaker })}
                      className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                    >
                      <option value="agent">Agent</option>
                      <option value="customer">Customer</option>
                      <option value="any">Any</option>
                    </select>
                  </label>
                  )}

                  {form.rule_type === 'call_metadata' && (
                    <>
                      <label className="sm:col-span-2">
                        <span className="block text-xs font-medium text-gray-700">Check type</span>
                        <select
                          value={form.cm_check_type}
                          onChange={(e) =>
                            setForm({ ...form, cm_check_type: e.target.value as CallMetadataCheck })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                        >
                          <option value="calling_hours">Calling hours (consumer's local time)</option>
                          <option value="max_duration">Max call duration</option>
                          <option value="min_duration">Min call duration</option>
                          <option value="frequency_cap">Frequency cap (calls per number per window)</option>
                        </select>
                      </label>

                      {form.cm_check_type === 'calling_hours' && (
                        <>
                          <label>
                            <span className="block text-xs font-medium text-gray-700">From hour (0–23)</span>
                            <input
                              type="number" min="0" max="23"
                              value={form.cm_from_hour}
                              onChange={(e) => setForm({ ...form, cm_from_hour: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                            />
                          </label>
                          <label>
                            <span className="block text-xs font-medium text-gray-700">To hour (0–23)</span>
                            <input
                              type="number" min="0" max="23"
                              value={form.cm_to_hour}
                              onChange={(e) => setForm({ ...form, cm_to_hour: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                            />
                            <span className="mt-0.5 block text-[10px] text-gray-500">
                              If From &gt; To (e.g., 22→6) the window crosses midnight.
                            </span>
                          </label>
                          <label className="sm:col-span-2">
                            <span className="block text-xs font-medium text-gray-700">Allowed weekdays</span>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {WEEKDAY_LABELS.map((label, idx) => {
                                const checked = form.cm_weekdays.includes(idx);
                                return (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() =>
                                      setForm({
                                        ...form,
                                        cm_weekdays: checked
                                          ? form.cm_weekdays.filter((d) => d !== idx)
                                          : [...form.cm_weekdays, idx].sort(),
                                      })
                                    }
                                    className={`rounded-md px-2 py-1 text-xs ring-1 ring-inset ${
                                      checked
                                        ? 'bg-indigo-600 text-white ring-indigo-600'
                                        : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50'
                                    }`}
                                  >
                                    {label}
                                  </button>
                                );
                              })}
                            </div>
                            <span className="mt-0.5 block text-[10px] text-gray-500">
                              Empty = all days allowed.
                            </span>
                          </label>
                          <label>
                            <span className="block text-xs font-medium text-gray-700">
                              Consumer timezone field (dotted path)
                            </span>
                            <input
                              value={form.cm_consumer_tz_field}
                              onChange={(e) =>
                                setForm({ ...form, cm_consumer_tz_field: e.target.value })
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 font-mono text-xs"
                              placeholder="metadata.consumer_timezone"
                            />
                          </label>
                          <label>
                            <span className="block text-xs font-medium text-gray-700">
                              Fallback timezone (IANA)
                            </span>
                            <select
                              value={form.cm_fallback_tz}
                              onChange={(e) => setForm({ ...form, cm_fallback_tz: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                            >
                              {COMMON_TIMEZONES.map((tz) => (
                                <option key={tz} value={tz}>{tz}</option>
                              ))}
                            </select>
                          </label>
                        </>
                      )}

                      {(form.cm_check_type === 'max_duration' ||
                        form.cm_check_type === 'min_duration') && (
                        <label className="sm:col-span-2">
                          <span className="block text-xs font-medium text-gray-700">
                            Threshold (seconds)
                          </span>
                          <input
                            type="number" min="1"
                            value={form.cm_seconds_threshold}
                            onChange={(e) =>
                              setForm({ ...form, cm_seconds_threshold: e.target.value })
                            }
                            className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                            placeholder="900 = 15 minutes"
                          />
                          <span className="mt-0.5 block text-[10px] text-gray-500">
                            {form.cm_check_type === 'max_duration'
                              ? 'Calls longer than this fire the rule.'
                              : 'Calls shorter than this fire the rule.'}
                          </span>
                        </label>
                      )}

                      {/* placeholder: frequency_cap fields rendered below */}
                      {form.cm_check_type === 'frequency_cap' && (
                        <>
                          <label>
                            <span className="block text-xs font-medium text-gray-700">
                              Max calls per window
                            </span>
                            <input
                              type="number" min="1"
                              value={form.cm_max_calls_per_window}
                              onChange={(e) =>
                                setForm({ ...form, cm_max_calls_per_window: e.target.value })
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                            />
                          </label>
                          <label>
                            <span className="block text-xs font-medium text-gray-700">
                              Window (days)
                            </span>
                            <input
                              type="number" min="1" max="365"
                              value={form.cm_window_days}
                              onChange={(e) => setForm({ ...form, cm_window_days: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                            />
                          </label>
                          <label className="sm:col-span-2">
                            <span className="block text-xs font-medium text-gray-700">
                              Group by
                            </span>
                            <select
                              value={form.cm_grouping}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  cm_grouping: e.target.value as 'phone_number' | 'customer_id',
                                })
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                            >
                              <option value="phone_number">Phone number</option>
                              <option value="customer_id">Customer ID</option>
                            </select>
                          </label>
                        </>
                      )}
                    </>
                  )}

                  {form.rule_type === 'llm_question' && (
                    <>
                      <label className="sm:col-span-2">
                        <span className="block text-xs font-medium text-gray-700">
                          Yes/no question for the LLM
                        </span>
                        <textarea
                          value={form.llm_prompt}
                          onChange={(e) => setForm({ ...form, llm_prompt: e.target.value })}
                          rows={3}
                          className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                          placeholder="e.g., Did the agent inform the customer about their right to cancel?"
                        />
                        <span className="mt-0.5 block text-[10px] text-gray-500">
                          Phrase it so the model can answer yes or no based only on the transcript.
                        </span>
                      </label>
                      <label>
                        <span className="block text-xs font-medium text-gray-700">
                          Expected answer (compliant value)
                        </span>
                        <select
                          value={form.llm_expected_value}
                          onChange={(e) =>
                            setForm({ ...form, llm_expected_value: e.target.value as 'yes' | 'no' })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                        >
                          <option value="yes">Yes (rule fires when model says no)</option>
                          <option value="no">No (rule fires when model says yes)</option>
                        </select>
                      </label>
                      <label>
                        <span className="block text-xs font-medium text-gray-700">Speaker focus</span>
                        <select
                          value={form.llm_speaker_focus}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              llm_speaker_focus: e.target.value as 'agent' | 'customer' | 'both',
                            })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                        >
                          <option value="agent">Agent</option>
                          <option value="customer">Customer</option>
                          <option value="both">Both</option>
                        </select>
                      </label>
                      <div className="sm:col-span-2 rounded-md bg-indigo-50 p-3 text-xs text-indigo-900 ring-1 ring-inset ring-indigo-200">
                        <p className="font-medium">Estimated cost added per call:</p>
                        <p className="mt-0.5 font-mono">
                          {(() => {
                            const inT = parseInt(form.llm_estimated_input_tokens || '0', 10);
                            const outT = parseInt(form.llm_estimated_output_tokens || '0', 10);
                            const cost = (inT * LLM_INPUT_PER_M + outT * LLM_OUTPUT_PER_M) / 1_000_000;
                            return `~$${cost.toFixed(8)} per call (Gemini Flash-Lite batch).`;
                          })()}
                        </p>
                        <p className="mt-1">
                          At 50,000 calls/month:{' '}
                          {(() => {
                            const inT = parseInt(form.llm_estimated_input_tokens || '0', 10);
                            const outT = parseInt(form.llm_estimated_output_tokens || '0', 10);
                            const cost =
                              (50_000 * (inT * LLM_INPUT_PER_M + outT * LLM_OUTPUT_PER_M)) /
                              1_000_000;
                            return `~$${cost.toFixed(2)}/month`;
                          })()}{' '}
                          — passed through on the customer's bill.
                        </p>
                      </div>
                    </>
                  )}

                  {(form.rule_type === 'phrase_required' || form.rule_type === 'phrase_forbidden') && (
                    <>
                  <label className="sm:col-span-2">
                    <span className="block text-xs font-medium text-gray-700">
                      Phrases (one per line — semantic match by default)
                    </span>
                    <textarea
                      value={form.phrases}
                      onChange={(e) => setForm({ ...form, phrases: e.target.value })}
                      rows={5}
                      className="mt-1 block w-full rounded-md border-gray-300 text-sm font-mono"
                      placeholder={"this call is being recorded\nesta llamada se está grabando"}
                    />
                  </label>

                  <label>
                    <span className="block text-xs font-medium text-gray-700">Matcher</span>
                    <select
                      value={form.matcher}
                      onChange={(e) => setForm({ ...form, matcher: e.target.value as PhraseMatcher })}
                      className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                    >
                      <option value="semantic">Semantic (default)</option>
                      <option value="exact">Exact</option>
                      <option value="regex">Regex</option>
                    </select>
                  </label>
                  </>
                  )}

                  {form.rule_type === 'phrase_required' && (
                    <label>
                      <span className="block text-xs font-medium text-gray-700">
                        Must appear within (seconds)
                      </span>
                      <input
                        type="number"
                        min="1"
                        max="3600"
                        value={form.must_appear_within_seconds}
                        onChange={(e) =>
                          setForm({ ...form, must_appear_within_seconds: e.target.value })
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                        placeholder="leave blank for no time limit"
                      />
                    </label>
                  )}

                  {form.rule_type === 'phrase_forbidden' && (
                    <>
                      <label>
                        <span className="block text-xs font-medium text-gray-700">Pattern type</span>
                        <select
                          value={form.pattern_type}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              pattern_type: e.target.value as 'literal' | 'regex',
                            })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                        >
                          <option value="literal">Literal</option>
                          <option value="regex">Regex (e.g. card numbers)</option>
                        </select>
                      </label>
                      <label>
                        <span className="block text-xs font-medium text-gray-700">
                          Only check after (seconds)
                        </span>
                        <input
                          type="number"
                          min="0"
                          max="3600"
                          value={form.only_after_seconds}
                          onChange={(e) =>
                            setForm({ ...form, only_after_seconds: e.target.value })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                          placeholder="0 = check whole call"
                        />
                      </label>
                    </>
                  )}

                  <label className="sm:col-span-2">
                    <span className="block text-xs font-medium text-gray-700">
                      Jurisdictions (comma-separated ISO codes, e.g. MX,BR)
                    </span>
                    <input
                      value={form.jurisdictions}
                      onChange={(e) => setForm({ ...form, jurisdictions: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                      placeholder="MX,BR"
                    />
                  </label>

                  <fieldset className="sm:col-span-2 rounded-md border border-gray-200 p-3">
                    <legend className="px-1 text-xs font-medium text-gray-700">
                      Regulation reference (optional, but strongly recommended for audit reports)
                    </legend>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <label>
                        <span className="block text-xs text-gray-600">Jurisdiction</span>
                        <input
                          value={form.regulation_jurisdiction}
                          onChange={(e) =>
                            setForm({ ...form, regulation_jurisdiction: e.target.value })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 text-xs"
                          placeholder="MX"
                        />
                      </label>
                      <label>
                        <span className="block text-xs text-gray-600">Law / Regulator</span>
                        <input
                          value={form.regulation_law}
                          onChange={(e) => setForm({ ...form, regulation_law: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 text-xs"
                          placeholder="CONDUSEF"
                        />
                      </label>
                      <label>
                        <span className="block text-xs text-gray-600">Citation</span>
                        <input
                          value={form.regulation_citation}
                          onChange={(e) =>
                            setForm({ ...form, regulation_citation: e.target.value })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 text-xs"
                          placeholder="Disposiciones de cobranza"
                        />
                      </label>
                    </div>
                  </fieldset>
                </div>

                {error && (
                  <div className="mx-5 mb-3 flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
                    <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="flex items-center justify-end gap-2 border-t border-gray-200 p-4">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={saving}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || !dirty}
                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create rule'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
